import React, { useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Edit,
  Delete,
  Person,
  PersonAdd,
  PersonOffOutlined,
  PersonOutlined,
  ContentCopy,
  Download,
} from '@mui/icons-material';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import Copyable from '@/components/Copyable/Copyable';
import DataTable from '@/components/DataTable/DataTable';

import UserFormDialog from './components/UserFormDialog';

import { API_BASE_URL } from '@/config';
import axios from 'axios';
import { formatTimestamp } from '@/utils/date/date';
import { FacilitiesApi } from '@/api/clients/facilities/facilities.api';
import { OrganizationsApi } from '@/api/clients/organizations/organizations.api';
import { UsersApi } from '@/api/clients/users/users.api';
import { useConfirmation } from '@/context/ConfirmationContext';
import { useExportFacilityUsers } from '@/api/hooks/users/useExportFacilityUsers';
import FacilityUserRoleColumn from '@/components/FacilityUserRoleColumn/FacilityUserRoleColumn';
import FacilityUserStatusChip from '@/components/FacilityUserStatusChip/FacilityUserStatusChip';
import FacilityAccessChips from '@/components/FacilityAccessChips/FacilityAccessChips';
import FacilityUserStatusText from '@/components/FacilityUserStatusChip/FacilityUserStatusText';
import { USER_STATUS } from './components/userHistory.utils';

const UserManagement = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const dataTableRef = useRef(null);
  const { requestConfirmation } = useConfirmation();

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formDialogOpen, setFormDialogOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isCopyingEmails, setIsCopyingEmails] = useState(false);

  const { exportUsers, isExporting } = useExportFacilityUsers();

  const handleCreateUser = () => {
    setFormDialogOpen(true);
  };

  const handleEditUser = (userId) => {
    navigate(`/admin/usermanagement/${userId}`);
  };

  const handleDeleteUser = (userId) => {
    const confirmMessage = 'Are you sure you want to delete this user?';

    requestConfirmation(confirmMessage, () => confirmDeleteUser(userId));
  };

  const confirmDeleteUser = async (userId) => {
    try {
      setLoading(true);
      await axios.delete(API_BASE_URL + '/delete_facility_user/' + userId);

      dataTableRef.current?.refresh();

      setSuccessMessage('User deleted successfully');
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 5000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setErrorMessage('Failed to delete user. Please try again.');
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUser = (userId, disableUser) => {
    const action = disableUser ? 'disable' : 'enable';
    const confirmMessage = `Are you sure you want to ${action} this user?`;

    requestConfirmation(confirmMessage, () =>
      confirmToggleUser(userId, disableUser),
    );
  };

  const confirmToggleUser = async (userId, disableUser) => {
    try {
      setLoading(true);
      await axios.put(API_BASE_URL + '/toggle_facility_user', {
        disableUser,
        id: userId,
      });

      dataTableRef.current?.refresh();

      const action = disableUser ? 'disabled' : 'enabled';
      setSuccessMessage(`User ${action} successfully`);
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 5000);
    } catch (err) {
      console.error('Error toggling user status:', err);
      const action = disableUser ? 'disabling' : 'enabling';
      setErrorMessage(`Failed to ${action} user. Please try again.`);
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async (userData) => {
    try {
      setLoading(true);

      const response = await axios.post(
        API_BASE_URL + '/createFacilityUser',
        userData,
      );
      const newUser = response.data?.data || response.data;

      setFormDialogOpen(false);

      if (newUser?.id) {
        navigate(`/admin/usermanagement/${newUser.id}`);
        return;
      }

      setSuccessMessage('User created successfully');
      setShowSuccessAlert(true);
      setTimeout(() => setShowSuccessAlert(false), 5000);
    } catch (err) {
      console.error('Error saving user:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAllEmails = async () => {
    setIsCopyingEmails(true);
    try {
      const filters = {};
      const organizationParam = searchParams.get('organization_id');
      const facilityParam = searchParams.get('facility_id');

      if (organizationParam) {
        filters.organization_id = parseInt(organizationParam);
      }
      if (facilityParam) {
        filters.facility_id = parseInt(facilityParam);
      }

      const response = await UsersApi.getFacilityUserEmails(filters);
      const emails = response.data;

      if (!emails || emails.length === 0) {
        toast.info(t('facilityUserManagement.noEmailsToCopy'));
        return;
      }

      const emailString = emails.join(', ');
      await navigator.clipboard.writeText(emailString);

      toast.success(
        t('facilityUserManagement.copyAllEmailsSuccess', {
          count: emails.length,
        }),
      );
    } catch (error) {
      console.error('Failed to copy emails:', error);
      toast.error(t('facilityUserManagement.copyAllEmailsError'));
    } finally {
      setIsCopyingEmails(false);
    }
  };

  const handleExportUsers = () => {
    const filters = {};
    const organizationParam = searchParams.get('organization_id');
    const facilityParam = searchParams.get('facility_id');
    const statusParam = searchParams.get('status');
    const searchParam = searchParams.get('search');

    if (organizationParam) {
      filters.organization_id = parseInt(organizationParam);
    }
    if (facilityParam) {
      filters.facility_id = parseInt(facilityParam);
    }
    if (statusParam) {
      filters.status = statusParam;
    }
    if (searchParam) {
      filters.search = searchParam;
    }

    exportUsers(filters);
  };

  const handleSuccessClose = () => {
    setShowSuccessAlert(false);
    setSuccessMessage('');
  };

  const handleErrorClose = () => {
    setShowErrorAlert(false);
    setErrorMessage('');
  };

  const userColumnFilters = useMemo(
    () => [
      {
        key: 'search',
        label: t('common.search'),
        filterType: 'search',
        placeholder: 'Search by email, name, or organization...',
        debounce: true,
        debounceDelay: 500,
      },
      {
        key: 'facility_id',
        label: t('facilityUserManagement.table.facilityColumn'),
        filterType: 'select',
        loadOptions: () => FacilitiesApi.getFacilities({ facility: true }),
        optionsTransform: (data) => [
          { value: '', label: 'All Facilities' },
          ...data.map((facility) => ({
            value: facility.id.toString(),
            label: `${facility.name} - ${facility.id}`,
          })),
        ],
      },
      {
        key: 'status',
        label: 'Status',
        filterType: 'select',
        options: [
          { value: '', label: t('facilityUserManagement.status.allUsers') },
          {
            value: String(USER_STATUS.ACTIVE),
            label: t('facilityUserManagement.status.active'),
          },
          {
            value: String(USER_STATUS.INACTIVE),
            label: t('facilityUserManagement.status.inactive'),
          },
          {
            value: String(USER_STATUS.ONBOARDING),
            label: t('facilityUserManagement.status.onboarding'),
          },
        ],
      },
      {
        key: 'organization_id',
        label: t('facilityUserManagement.table.organizationColumn'),
        filterType: 'select',
        loadOptions: () => OrganizationsApi.getOrganizations(),
        optionsTransform: (data) => [
          { value: '', label: 'All Organizations' },
          ...data.organizations.map((org) => ({
            value: org.id.toString(),
            label: org.name,
          })),
        ],
      },
    ],
    [t],
  );

  const userColumns = useMemo(
    () => [
      {
        key: 'email',
        label: t('facilityUserManagement.table.emailColumn'),
        sortable: true,
        render: (value, row) => (
          <FacilityUserStatusText status={row.status}>
            <Copyable value={value}>{value}</Copyable>
          </FacilityUserStatusText>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        render: (_value, row) => <FacilityUserStatusChip status={row.status} />,
      },
      {
        key: 'name',
        label: t('facilityUserManagement.table.usernameColumn'),
        sortable: true,
        render: (value, row) => (
          <FacilityUserStatusText status={row.status}>
            {value}
          </FacilityUserStatusText>
        ),
      },
      {
        key: 'role_name',
        label: t('facilityUserManagement.table.roleColumn', 'Role'),
        sortable: true,
        render: (_value, row) => <FacilityUserRoleColumn user={row} />,
      },
      {
        key: 'organization_name',
        label: t('facilityUserManagement.table.organizationColumn'),
        sortable: true,
        render: (value, row) => (
          <FacilityUserStatusText status={row.status}>
            {value || '-'}
          </FacilityUserStatusText>
        ),
      },
      {
        key: 'facilities',
        label: t('facilityUserManagement.table.facilityColumn'),
        sortable: false,
        render: (value, row) => (
          <FacilityAccessChips
            facilities={value}
            hasAllFacilitiesAccess={row.has_all_facilities_access}
          />
        ),
      },
      {
        key: 'last_sign_in_at',
        label: t('facilityUserManagement.table.lastSignInColumn'),
        sortable: true,
        render: (value, row) => (
          <FacilityUserStatusText status={row.status}>
            {formatTimestamp(value)}
          </FacilityUserStatusText>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        sortable: false,
        render: (_value, row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title='Edit User'>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditUser(row.id);
                }}
                disabled={loading}>
                <Edit fontSize='small' />
              </IconButton>
            </Tooltip>
            {row.status === USER_STATUS.ACTIVE ? (
              <Tooltip title='Disable User'>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleUser(row.id, true);
                  }}
                  disabled={loading}>
                  <PersonOffOutlined fontSize='small' color='warning' />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title='Enable User'>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleUser(row.id, false);
                  }}
                  disabled={loading}>
                  <PersonOutlined fontSize='small' color='success' />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title='Delete User'>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteUser(row.id);
                }}
                disabled={loading}>
                <Delete fontSize='small' color='error' />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [t, loading],
  );

  return (
    <>
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Person sx={{ color: 'white' }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500, flex: 1 }}
                component='h4'>
                {t('facilityUserManagement.title')}
              </Typography>
              <Tooltip title={t('facilityUserManagement.exportTooltip')} arrow>
                <span>
                  <Button
                    startIcon={<Download sx={{ color: 'white' }} />}
                    onClick={handleExportUsers}
                    disabled={isExporting}
                    sx={{
                      color: 'white',
                      borderColor: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: 'white',
                      },
                    }}
                    variant='outlined'>
                    {isExporting
                      ? t('common.exporting')
                      : t('facilityUserManagement.exportButton')}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={t('facilityUserManagement.copyAllEmailsTooltip')}
                arrow>
                <span>
                  <Button
                    startIcon={<ContentCopy sx={{ color: 'white' }} />}
                    onClick={handleCopyAllEmails}
                    disabled={isCopyingEmails}
                    sx={{
                      color: 'white',
                      borderColor: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: 'white',
                      },
                    }}
                    variant='outlined'>
                    {isCopyingEmails
                      ? 'Copying...'
                      : t('facilityUserManagement.copyAllEmailsButton')}
                  </Button>
                </span>
              </Tooltip>
              <Button
                startIcon={<PersonAdd sx={{ color: 'white' }} />}
                onClick={handleCreateUser}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'white',
                  },
                }}
                variant='outlined'>
                {t('facilityUserManagement.addUsersButton')}
              </Button>
            </Box>
          </CardHeader>
        }>
        {showSuccessAlert && successMessage && (
          <Alert severity='success' sx={{ mb: 3 }} onClose={handleSuccessClose}>
            {successMessage}
          </Alert>
        )}

        {showErrorAlert && errorMessage && (
          <Alert severity='error' sx={{ mb: 3 }} onClose={handleErrorClose}>
            {errorMessage}
          </Alert>
        )}

        <DataTable
          ref={dataTableRef}
          columns={userColumns}
          endpoint='/users/facility'
          initialSort={{ key: 'created_at', order: 'desc' }}
          pageSize={50}
          autoFetch={true}
          enableColumnPicker={true}
          enableSorting={true}
          enableFilters={true}
          enablePinning={false}
          persistenceKey='facility-users'
          enableUrlPersistence={true}
          columnFilters={userColumnFilters}
          renderEmptyState={() => (
            <tr>
              <td
                colSpan={userColumns.length}
                style={{ textAlign: 'center', padding: '3rem' }}>
                <Typography variant='body1' color='text.secondary'>
                  No users found
                </Typography>
              </td>
            </tr>
          )}
        />
      </PageLayout>

      <UserFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSave={handleSaveUser}
        loading={loading}
      />
    </>
  );
};

export default UserManagement;
